import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Input, InputNumber, Space, Typography, message, Row, Col,
    Tag, Select, Modal, Form, DatePicker, AutoComplete, Divider
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, ShoppingOutlined, SearchOutlined,
    ExclamationCircleOutlined, BarcodeOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;
const { confirm } = Modal;

const Buy = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    // New purchase form state
    const [purchaseItems, setPurchaseItems] = useState([]);
    const [items, setItems] = useState([]);
    const [itemSearch, setItemSearch] = useState('');
    const [itemOptions, setItemOptions] = useState([]);
    const [currency, setCurrency] = useState('IQD');

    useEffect(() => {
        fetchPurchases();
        fetchItems();
    }, []);

    const fetchPurchases = async () => {
        try {
            setLoading(true);
            const res = await api.get('/pharmacy/purchases');
            setPurchases(res.data.data || []);
        } catch (error) {
            message.error(t('pharmacy:messages.loadPurchasesFailed'));
        } finally {
            setLoading(false);
        }
    };

    const fetchItems = async () => {
        try {
            const res = await api.get('/pharmacy/items');
            setItems(res.data.data || []);
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    };

    const handleItemSearch = (search) => {
        setItemSearch(search);
        if (!search) {
            setItemOptions([]);
            return;
        }
        const filtered = items.filter(i =>
            i.itemName.toLowerCase().includes(search.toLowerCase()) ||
            (i.barcode && i.barcode.includes(search)) ||
            i.sku.toLowerCase().includes(search.toLowerCase())
        );
        setItemOptions(filtered.map(i => ({
            value: i._id,
            label: `${i.itemName} (SKU: ${i.sku})${i.barcode ? ` [${i.barcode}]` : ''}`,
            item: i
        })));
    };

    const addPurchaseItem = (itemId, option) => {
        const item = option.item;
        if (purchaseItems.find(pi => pi.pharmacyItemId === item._id)) {
            message.warning(t('pharmacy:messages.itemAlreadyAdded'));
            return;
        }
        setPurchaseItems([...purchaseItems, {
            pharmacyItemId: item._id,
            itemName: item.itemName,
            barcode: item.barcode,
            sku: item.sku,
            quantity: 1,
            costPrice: item.costPrice,
            total: item.costPrice
        }]);
        setItemSearch('');
        setItemOptions([]);
    };

    const updatePurchaseItem = (index, field, value) => {
        const updated = [...purchaseItems];
        updated[index][field] = value;
        updated[index].total = updated[index].quantity * updated[index].costPrice;
        setPurchaseItems(updated);
    };

    const removePurchaseItem = (index) => {
        setPurchaseItems(purchaseItems.filter((_, i) => i !== index));
    };

    const purchaseTotal = purchaseItems.reduce((sum, item) => sum + item.total, 0);

    const handleCreatePurchase = async () => {
        try {
            const values = await form.validateFields();
            if (purchaseItems.length === 0) {
                message.error(t('pharmacy:messages.addAtLeastOne'));
                return;
            }

            setSaving(true);
            const data = {
                ...values,
                purchaseDate: values.purchaseDate?.toISOString() || new Date().toISOString(),
                items: purchaseItems,
                totalAmount: purchaseTotal,
                currency
            };

            await api.post('/pharmacy/purchases', data);
            message.success(t('pharmacy:messages.purchaseRecorded'));
            setModalVisible(false);
            setPurchaseItems([]);
            form.resetFields();
            fetchPurchases();
            fetchItems(); // Refresh inventory data
        } catch (error) {
            if (error.errorFields) return;
            message.error(error.response?.data?.message || t('pharmacy:messages.purchaseFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeletePurchase = (record) => {
        confirm({
            title: t('pharmacy:buy.deletePurchaseTitle', { number: record.purchaseNumber }),
            icon: <ExclamationCircleOutlined />,
            content: t('pharmacy:buy.deletePurchaseContent'),
            okType: 'danger',
            onOk: async () => {
                try {
                    await api.delete(`/pharmacy/purchases/${record._id}`);
                    message.success(t('pharmacy:messages.purchaseDeleted'));
                    fetchPurchases();
                } catch (error) {
                    message.error(t('pharmacy:messages.deletePurchaseFailed'));
                }
            }
        });
    };

    const openNewPurchase = () => {
        setPurchaseItems([]);
        form.resetFields();
        form.setFieldsValue({
            paymentMethod: 'Cash',
            paymentStatus: 'Paid',
            purchaseDate: dayjs()
        });
        setModalVisible(true);
    };

    const purchaseColumns = [
        {
            title: t('pharmacy:buy.purchaseNumber'),
            dataIndex: 'purchaseNumber',
            key: 'purchaseNumber',
            render: (num) => <Tag color="blue">{num}</Tag>
        },
        {
            title: t('pharmacy:common.supplier'),
            dataIndex: 'supplier',
            key: 'supplier',
        },
        {
            title: t('pharmacy:common.items'),
            dataIndex: 'items',
            key: 'items',
            render: (items) => items?.length || 0
        },
        {
            title: t('pharmacy:common.total'),
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount, record) => (
                <Text strong>
                    {record.currency === 'IQD' ? `IQD ${amount.toLocaleString()}` : `$${amount.toFixed(2)}`}
                </Text>
            )
        },
        {
            title: t('pharmacy:common.payment'),
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => <Tag color={status === 'Paid' ? 'green' : 'orange'}>{t(`pharmacy:common.paymentStatuses.${status}`, status)}</Tag>
        },
        {
            title: t('pharmacy:common.date'),
            dataIndex: 'purchaseDate',
            key: 'purchaseDate',
            render: (date) => dayjs(date).format('MMM DD, YYYY')
        },
        {
            title: t('pharmacy:common.invoiceNum'),
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            render: (num) => num || '-'
        },
        {
            title: t('pharmacy:common.actions'),
            key: 'actions',
            render: (_, record) => (
                <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeletePurchase(record)}>
                    {t('pharmacy:common.delete')}
                </Button>
            )
        }
    ];

    const purchaseItemColumns = [
        {
            title: t('pharmacy:common.number'),
            render: (_, __, index) => index + 1,
            width: 40
        },
        {
            title: t('pharmacy:common.item'),
            dataIndex: 'itemName',
            key: 'itemName',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    <div><Text type="secondary" style={{ fontSize: 11 }}>SKU: {record.sku} {record.barcode ? `| ${record.barcode}` : ''}</Text></div>
                </div>
            )
        },
        {
            title: t('pharmacy:common.qty'),
            dataIndex: 'quantity',
            key: 'quantity',
            width: 90,
            render: (qty, _, index) => (
                <InputNumber min={1} value={qty} size="small" onChange={(val) => updatePurchaseItem(index, 'quantity', val)} />
            )
        },
        {
            title: t('pharmacy:common.costPrice'),
            dataIndex: 'costPrice',
            key: 'costPrice',
            width: 120,
            render: (price, _, index) => (
                <InputNumber min={0} value={price} size="small" onChange={(val) => updatePurchaseItem(index, 'costPrice', val)} />
            )
        },
        {
            title: t('pharmacy:common.total'),
            dataIndex: 'total',
            key: 'total',
            width: 120,
            render: (total) => (
                <Text strong>
                    {currency === 'IQD' ? `IQD ${total.toLocaleString()}` : `$${total.toFixed(2)}`}
                </Text>
            )
        },
        {
            title: '',
            key: 'action',
            width: 50,
            render: (_, __, index) => (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removePurchaseItem(index)} />
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}><ShoppingOutlined /> {t('pharmacy:buy.title')}</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={openNewPurchase}>
                    {t('pharmacy:buy.newPurchase')}
                </Button>
            </div>

            <Card>
                <Table
                    columns={purchaseColumns}
                    dataSource={purchases}
                    rowKey="_id"
                    loading={loading}
                    locale={{ emptyText: t('pharmacy:buy.noPurchases') }}
                    pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total) => t('pharmacy:buy.totalCount', { total }) }}
                    scroll={{ x: 'max-content' }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <Table
                                dataSource={record.items}
                                columns={[
                                    { title: t('pharmacy:common.item'), dataIndex: 'itemName', key: 'itemName' },
                                    { title: t('pharmacy:common.qty'), dataIndex: 'quantity', key: 'quantity' },
                                    {
                                        title: t('pharmacy:common.costPrice'),
                                        dataIndex: 'costPrice',
                                        key: 'costPrice',
                                        render: (p) => record.currency === 'IQD' ? `IQD ${p.toLocaleString()}` : `$${p.toFixed(2)}`
                                    },
                                    {
                                        title: t('pharmacy:common.total'),
                                        dataIndex: 'total',
                                        key: 'total',
                                        render: (t) => record.currency === 'IQD' ? `IQD ${t.toLocaleString()}` : `$${t.toFixed(2)}`
                                    }
                                ]}
                                rowKey="pharmacyItemId"
                                pagination={false}
                                size="small"
                            />
                        )
                    }}
                />
            </Card>

            {/* New Purchase Modal */}
            <Modal
                title={t('pharmacy:buy.newPurchase')}
                open={modalVisible}
                onOk={handleCreatePurchase}
                onCancel={() => setModalVisible(false)}
                width={900}
                confirmLoading={saving}
                okText={t('pharmacy:buy.recordPurchase')}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="supplier" label={t('pharmacy:common.supplier')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item label={t('pharmacy:common.currency')}>
                                <Select value={currency} onChange={setCurrency}>
                                    <Select.Option value="IQD">IQD</Select.Option>
                                    <Select.Option value="USD">USD</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="paymentMethod" label={t('pharmacy:common.payment')}>
                                <Select>
                                    <Select.Option value="Cash">{t('pharmacy:common.paymentMethods.Cash')}</Select.Option>
                                    <Select.Option value="Credit Card">{t('pharmacy:common.paymentMethods.Credit Card')}</Select.Option>
                                    <Select.Option value="Bank Transfer">{t('pharmacy:common.paymentMethods.Bank Transfer')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="paymentStatus" label={t('pharmacy:common.status')}>
                                <Select>
                                    <Select.Option value="Paid">{t('pharmacy:common.paymentStatuses.Paid')}</Select.Option>
                                    <Select.Option value="Pending">{t('pharmacy:common.paymentStatuses.Pending')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="purchaseDate" label={t('pharmacy:common.date')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="invoiceNumber" label={t('pharmacy:buy.supplierInvoice')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="notes" label={t('pharmacy:common.notes')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                <Divider>{t('pharmacy:buy.itemsDivider')}</Divider>

                {/* Item Search */}
                <div style={{ marginBottom: 16 }}>
                    <AutoComplete
                        style={{ width: '100%' }}
                        options={itemOptions}
                        value={itemSearch}
                        onSearch={handleItemSearch}
                        onSelect={addPurchaseItem}
                        placeholder={t('pharmacy:buy.searchItem')}
                    >
                        <Input prefix={<SearchOutlined />} size="large" />
                    </AutoComplete>
                </div>

                <Table
                    dataSource={purchaseItems}
                    columns={purchaseItemColumns}
                    rowKey="pharmacyItemId"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: t('pharmacy:buy.searchAndAdd') }}
                />

                <Divider />
                <Row justify="end">
                    <Col>
                        <Text strong style={{ fontSize: 18 }}>
                            {t('pharmacy:buy.totalLabel')} {currency === 'IQD' ? `IQD ${purchaseTotal.toLocaleString()}` : `$${purchaseTotal.toFixed(2)}`}
                        </Text>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
};

export default Buy;
